import type { FormEvent, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Field, FieldLabel } from "@/frontend/components/ui/field";

type NameStepProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
  submitting: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  error?: string | null;
  footer?: ReactNode;
};

/**
 * Presentational "What's your name?" screen shared by the login name step and
 * the post-sign-in profile-completion gate. Owns only markup; each caller wires
 * its own submit behavior and optional footer.
 */
export function NameStep({
  value,
  onChange,
  onSubmit,
  submitting,
  submitLabel = "Continue",
  submittingLabel = "Saving...",
  error,
  footer,
}: NameStepProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-1 text-center">
        <h1 className="text-2xl font-bold">What's your name?</h1>
        <p className="text-sm text-muted-foreground">Tell us how to address you.</p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="name">Full Name</FieldLabel>
          <Input
            id="name"
            type="text"
            placeholder="Jane Smith"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required
            autoFocus
            autoComplete="name"
            className="h-11"
          />
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={submitting} className="h-11 w-full bg-black text-white hover:bg-gray-900">
          {submitting ? (
            <><Loader2 className="animate-spin" data-icon="inline-start" />{submittingLabel}</>
          ) : (
            submitLabel
          )}
        </Button>
        {footer}
      </form>
    </div>
  );
}
